// src/pages/common/reports/R0006/services/kpiService.js

import { R0006_CONFIG } from '../constants';
import { getClientBusinessRules } from '../utils/clientConfigUtils';

export const calculateKPIValues = (selectedArea, daysCount = 1, mockReportData = null) => {
  // If we have report data, calculate from actual table data
  if (mockReportData?.restaurants) {
    const restaurants = mockReportData.restaurants;
    
    // Calculate totals from actual table data
    const totals = restaurants.reduce(
      (acc, restaurant) => ({
        totalActual: acc.totalActual + (restaurant.actSales || 0),
        totalProjected: acc.totalProjected + (restaurant.projSales || 0),
        totalOpportunity: acc.totalOpportunity + (restaurant.opportunity || 0),
        positiveVarianceCount: acc.positiveVarianceCount + 
          ((restaurant.actSales || 0) >= (restaurant.projSales || 0) ? 1 : 0),
        totalStores: acc.totalStores + 1
      }),
      {
        totalActual: 0,
        totalProjected: 0,
        totalOpportunity: 0,
        positiveVarianceCount: 0,
        totalStores: 0
      }
    );
    
    // Calculate opportunity as percentage of actual sales
    const opportunityPercentage = totals.totalActual > 0 
      ? (totals.totalOpportunity / totals.totalActual) * 100
      : 0;
    
    return {
      // Total Sales - matches actual sales from table
      totalSales: Math.round(totals.totalActual),
      totalSalesProjected: Math.round(totals.totalProjected),
      
      // Performance - count of positive variance / total stores (as string format)
      performanceCount: `${totals.positiveVarianceCount}/${totals.totalStores}`,
      
      // Opportunity - dollar amount with percentage in parentheses
      opportunity: `$${Math.round(totals.totalOpportunity).toLocaleString()} (${(Math.round(opportunityPercentage * 10) / 10).toFixed(1)}%)`,
      
      // Keep raw values for reference and rating calculation
      opportunityDollarAmount: Math.round(totals.totalOpportunity),
      opportunityPercentage: Math.round(opportunityPercentage * 10) / 10,
      
      // Additional context for debugging
      meta: {
        selectedArea,
        daysCount,
        totalStores: totals.totalStores,
        storesAboveTarget: totals.positiveVarianceCount,
        calculatedFromTableData: true
      }
    };
  }
  
  // Fallback to original calculation if no report data
  const businessRules = getClientBusinessRules() || R0006_CONFIG.BUSINESS_RULES;
  
  const isBrand = selectedArea === 'Area Summary';
  const baseMultiplier = isBrand ?
    businessRules.BASE_MULTIPLIER.BRAND :
    businessRules.BASE_MULTIPLIER.OTHER;

  const dayMultiplier = daysCount || 1;

  // Get store counts based on area
  const storeCounts = isBrand ?
    businessRules.STORE_COUNTS.BRAND :
    businessRules.STORE_COUNTS.OTHER;

  // Demo: Make actual sales vary based on area to show both positive and negative variance
  const actualSales = Math.round(222000 * baseMultiplier * dayMultiplier);
  const projectedSales = isBrand
    ? Math.round(215000 * baseMultiplier * dayMultiplier) // Area Summary: actual > projected (positive)
    : Math.round(230000 * baseMultiplier * dayMultiplier); // Other areas: actual < projected (negative)
  
  // Calculate opportunity amount and convert to percentage
  const opportunityAmount = Math.round(3500 * baseMultiplier * dayMultiplier);
  const opportunityPercentage = actualSales > 0 
    ? (opportunityAmount / actualSales) * 100 
    : 0;
  
  return {
    // Total Sales - no rating, simple display
    totalSales: actualSales,
    totalSalesProjected: projectedSales,
    
    // Performance - count format for consistency
    performanceCount: `${storeCounts.aboveTarget}/${storeCounts.total}`,
    
    // Opportunity - dollar amount with percentage in parentheses
    opportunity: `$${opportunityAmount.toLocaleString()} (${(Math.round(opportunityPercentage * 10) / 10).toFixed(1)}%)`,
    
    // Keep raw values for reference and rating calculation
    opportunityDollarAmount: opportunityAmount,
    opportunityPercentage: Math.round(opportunityPercentage * 10) / 10,
    
    // Additional context for debugging
    meta: {
      selectedArea,
      daysCount,
      totalStores: storeCounts.total,
      storesAboveTarget: storeCounts.aboveTarget,
      calculatedFromTableData: false
    }
  };
};